import React from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { motion } from 'framer-motion';
import { FocusOn } from 'react-focus-on';
import Close from '@mui/icons-material/Close';

import { Button } from 'uikit/buttons';
import ModalHeader from './modalHeader';
import ModalContent from './modalContent';
import ModalActions from './modalActions';

import styles from '../styles/uikitModalsModal.module.scss';

const Modal = ({
  modal,
  formState,
  hasFormError,
  ariaLabel = '',
  closeButtonAriaLabel = '',
  isFocusLockDisabled = false,
  modalData = {},
  onActionClicked = () => {},
  onFormUpdate = () => {},
  onCloseButtonClicked = () => {},
  onFormLoad = () => {},
}) => {
  const ANIMATIONS = {
    overlay: {
      animate: { opacity: 1 },
      initial: { opacity: 0 },
      exit: { opacity: 0 },
      transition: { ease: 'easeInOut', duration: 0.1 },
    },
    modal: {
      regular: {
        animate: { y: 0, opacity: 1 },
        initial: { y: 10, opacity: 0 },
        exit: { opacity: 0 },
        transition: { ease: 'easeInOut', duration: 0.2 },
      },
      fullScreen: {
        animate: { opacity: 1 },
        initial: { opacity: 0 },
        exit: { opacity: 0 },
        transition: { ease: 'easeInOut', duration: 0.2 },
      },
    },
  };

  const {
    actions,
    className,
    closeButtonColor,
    dialogClassName,
    hasCloseButton,
    isFluid = true,
    isFullScreen,
  } = modal;

  const modalAnimation = ANIMATIONS.modal[isFullScreen ? 'fullScreen' : 'regular'];

  return (
    <FocusOn
      enabled={!isFocusLockDisabled}
      onEscapeKey={() => hasCloseButton && onCloseButtonClicked()}
      returnFocus
    >
      <div className={classnames(styles.element, className)} role="dialog" aria-label={ariaLabel}>
        {!isFullScreen && (
          <motion.div
            className={styles.overlay}
            animate={ANIMATIONS.overlay.animate}
            initial={ANIMATIONS.overlay.initial}
            exit={ANIMATIONS.overlay.exit}
            transition={ANIMATIONS.overlay.transition}
            data-testid="modal-overlay"
          />
        )}
        <motion.div
          className={classnames(styles.dialog, dialogClassName, {
            [styles.isFluid]: isFluid,
            [styles.isFullScreen]: isFullScreen,
          })}
          animate={modalAnimation.animate}
          initial={modalAnimation.initial}
          exit={modalAnimation.exit}
          transition={modalAnimation.transition}
        >
          {hasCloseButton && (
            <Button
              className={styles.close}
              color={closeButtonColor}
              variant="ghost"
              icon={<Close />}
              onClick={onCloseButtonClicked}
              size="small"
              ariaLabel={closeButtonAriaLabel || i18n.t('close', 'Close')}
            />
          )}
          <div className={styles.inner}>
            <ModalHeader modal={modal} onCloseButtonClicked={onCloseButtonClicked} />
            <ModalContent
              modal={modal}
              modalData={modalData}
              onFormLoad={onFormLoad}
              onFormUpdate={onFormUpdate}
              onCloseButtonClicked={onCloseButtonClicked}
              onActionClicked={onActionClicked}
            />
            <ModalActions
              actions={actions}
              formState={formState}
              modalData={modalData}
              hasFormError={hasFormError}
              isFullScreen={isFullScreen}
              onActionClicked={onActionClicked}
            />
          </div>
        </motion.div>
      </div>
    </FocusOn>
  );
};

Modal.propTypes = {
  ariaLabel: PropTypes.string,
  closeButtonAriaLabel: PropTypes.string,
  modal: PropTypes.shape({
    className: PropTypes.string,
    dialogClassName: PropTypes.string,
    closeButtonColor: PropTypes.string,
    data: PropTypes.shape({}),
    // eslint-disable-next-line react/forbid-prop-types
    actions: PropTypes.array,
    isFullScreen: PropTypes.bool,
    hasCloseButton: PropTypes.bool,
    isFluid: PropTypes.bool,
  }).isRequired,
  isFocusLockDisabled: PropTypes.bool,
  modalData: PropTypes.shape({}),
  formState: PropTypes.shape({}).isRequired,
  hasFormError: PropTypes.bool.isRequired,
  onActionClicked: PropTypes.func,
  onFormUpdate: PropTypes.func,
  onCloseButtonClicked: PropTypes.func,
  onFormLoad: PropTypes.func,
};

export default Modal;
